import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import RegistrationProgress from '../components/RegistrationProgress';
import axios from '../utils/axiosConfig';
import { ValidationProvider } from '../context/ValidationContext';
import CompetitionImage from '../components/CompetitionImage';
import { handleApiError } from '../utils/apiErrorHandler';

interface Competition {
  _id: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  enableOfficial: boolean;
}

interface ValidationItem {
  status: boolean;
  message: string;
}

// Create a context to share the registration validation state
export const RegistrationValidationContext = React.createContext<{
  errors: Record<string, ValidationItem>;
  warnings: Record<string, ValidationItem>;
  refreshValidation: () => void;
}>({
  errors: {},
  warnings: {},
  refreshValidation: () => {}
});

const CompetitionRegistrationPage: React.FC = () => {
  const { competitionId } = useParams();
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);
  const [registrationErrors, setRegistrationErrors] = useState<Record<string, ValidationItem>>({});
  const [registrationWarnings, setRegistrationWarnings] = useState<Record<string, ValidationItem>>({});
  const [hasLogo, setHasLogo] = useState<boolean>(false);
  const [validationVersion, setValidationVersion] = useState(0);

  // Create the refresh function that will be shared with child components
  const refreshValidation = useCallback(async () => {
    if (competitionId) {
      try {
        const response = await axios.get(`/api/competition-registrations/validate/${competitionId}`);
        
        setRegistrationErrors(response.data.errors || {});
        setRegistrationWarnings(response.data.warnings || {});
        setValidationVersion(prev => prev + 1); // Increment version to force rerender
      } catch (error) {
        handleApiError(error, 'Fehler beim Validieren der Meldung.');
      }
    }
  }, [competitionId]);

  // Memoize the context value to prevent unnecessary rerenders
  const validationContextValue = useMemo(() => ({
    errors: registrationErrors,
    warnings: registrationWarnings,
    refreshValidation
  }), [registrationErrors, registrationWarnings, refreshValidation, validationVersion]);

  useEffect(() => {
    // Fetch competition details
    if (competitionId) {
      setLoading(true);
      axios.get(`/api/competitions/${competitionId}`)
        .then(response => {
          setCompetition(response.data);
          // Check if logo exists
          axios.head(`/api/images/Competition/${competitionId}/icon`)
            .then(() => setHasLogo(true))
            .catch(() => setHasLogo(false));
          setLoading(false);
        })
        .catch(error => {
          handleApiError(error, 'Fehler beim Sammeln der Wettkämpfe.');
          setLoading(false);
        });
    }
  }, [competitionId]);

  // Example: Check for errors/warnings in registration
  useEffect(() => {
    if (competitionId) {
      refreshValidation();
    }
  }, [competitionId, refreshValidation]);

  if (loading) {
    return <Typography>Laden...</Typography>;
  }

  if (!competition) {
    return <Typography color="error">Wettkampf nicht gefunden</Typography>;
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box>
            <CompetitionImage 
              competitionId={competition._id} 
              hasImage={hasLogo}
              imageType="icon"
              size="large"
              useFullSize={true}
            />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom>
              {competition.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {competition.location} | {new Date(competition.startDate).toLocaleDateString()} - {new Date(competition.endDate).toLocaleDateString()} | Meldeschluss: {new Date(competition.registrationDeadline).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Pass error and warning states to RegistrationProgress with key to force rerender */}
      <RegistrationProgress
        key={`progress-${validationVersion}`}
        errors={registrationErrors}
        warnings={registrationWarnings}
        enableOfficial={competition.enableOfficial}
      />
      
      {/* Provide the validation context to all child components */}
      <RegistrationValidationContext.Provider value={validationContextValue}>
        <ValidationProvider>
          <Box sx={{ mt: 3 }}>
            <Outlet />
          </Box>
        </ValidationProvider>
      </RegistrationValidationContext.Provider>
    </Box>
  );
};

export default CompetitionRegistrationPage;